/**
 * Strip stray JSON blobs from assistant-facing text.
 *
 * Defense-in-depth against backend leaks where the LLM emits prose alongside
 * a structured JSON envelope (e.g. trailing `{"follow_up":"..."}` blocks,
 * fenced ```json ... ``` blocks, or a leading tool-call `{"tool":...}` object).
 *
 * The backend `_sanitize_assistant_text` should catch these first; this is
 * the last-ditch guard before the typewriter renders.
 */

/**
 * Walk forward from `text[start]` (which must be `{`) and return the index
 * just past the matching `}`, accounting for nested braces and string
 * literals. Returns -1 if no balanced block is found.
 */
function findBalancedEnd(text, start) {
  if (text[start] !== '{') return -1;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (inString) {
      if (escape) { escape = false; continue; }
      if (c === '\\') { escape = true; continue; }
      if (c === '"') { inString = false; }
      continue;
    }
    if (c === '"') { inString = true; continue; }
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

/**
 * Strip a leading `{"tool": ...}` blob if present. Other leading JSON is left
 * alone to avoid eating legitimate content.
 */
function stripLeadingToolCall(text) {
  const trimmed = text.replace(/^\s+/, '');
  if (!trimmed.startsWith('{')) return text;
  const probe = trimmed.slice(0, Math.min(120, trimmed.length));
  if (!/^\{\s*"tool"\s*:/.test(probe)) return text;
  const end = findBalancedEnd(trimmed, 0);
  if (end === -1) return text;
  const rest = trimmed.slice(end).replace(/^\s+/, '');
  return rest;
}

/**
 * Strip fenced ```json { ... } ``` blocks anywhere in the text, depth-aware.
 */
function stripFencedJson(text) {
  let result = text;
  const fenceRe = /```json\s*/gi;
  let safety = 0;
  while (safety++ < 16) {
    fenceRe.lastIndex = 0;
    const m = fenceRe.exec(result);
    if (!m) break;
    const braceIdx = result.indexOf('{', m.index + m[0].length);
    if (braceIdx === -1) break;
    const end = findBalancedEnd(result, braceIdx);
    if (end === -1) break;
    // Optional closing ```
    const after = result.slice(end);
    const closeMatch = after.match(/^\s*```/);
    const cutEnd = end + (closeMatch ? closeMatch[0].length : 0);
    result = result.slice(0, m.index) + result.slice(cutEnd);
  }
  return result;
}

/**
 * Strip a trailing bare JSON object on its own (e.g. `{"follow_up":"..."}`).
 * Iterates so multiple trailing objects all get removed.
 */
function stripTrailingJson(text) {
  let result = text;
  let safety = 0;
  while (safety++ < 8) {
    const trimmed = result.replace(/\s+$/, '');
    if (!trimmed.endsWith('}')) break;
    // Walk backwards counting braces (string-aware enough for typical LLM output).
    let depth = 0;
    let inString = false;
    let escape = false;
    let openIdx = -1;
    for (let i = trimmed.length - 1; i >= 0; i--) {
      const c = trimmed[i];
      // Backwards string tracking is approximate; rely on forward validation.
      if (c === '}') depth++;
      else if (c === '{') {
        depth--;
        if (depth === 0) {
          const end = findBalancedEnd(trimmed, i);
          if (end === trimmed.length) {
            openIdx = i;
          }
          break;
        }
      }
    }
    if (openIdx === -1) break;
    const proseBefore = trimmed.slice(0, openIdx).replace(/\s+$/, '');
    // If there's no prose before the JSON, the entire text IS the structured
    // envelope — leave it alone so downstream parsing still works.
    if (!proseBefore) break;
    result = proseBefore;
  }
  return result;
}

export function stripLeadingToolCallJson(text) {
  if (text == null) return text;
  let result = String(text);
  result = stripLeadingToolCall(result);
  result = stripFencedJson(result);
  result = stripTrailingJson(result);
  return result;
}
